import { HttpEvent, HttpEventType, HttpResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { filter, map } from "rxjs/operators";
import { validate as uuidValidate } from "uuid";
import { ApiService, FileResponse } from "source/app/configuration/services/api.service";
import { NotificationService } from "source/app/configuration/services/notification.service";
import { TranslocoService } from "@ngneat/transloco";
import { FileQuestion, FormQuestion } from "../../../checklist.interface";
import { QuestionComponent } from "../question.component";

function isLikelyFileDocumentId(value: string): boolean {
	if (value.startsWith("files/")) {
		return true;
	}
	if (uuidValidate(value)) {
		return true;
	}
	return /^[0-9a-f]{32}$/i.test(value);
}

@Component({
	selector: "app-file-question",
	templateUrl: "./file-question.component.html",
	styleUrls: ["./file-question.component.scss"],
	changeDetection: ChangeDetectionStrategy.Default,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FileQuestionComponent),
			multi: true,
		},
	],
})
export class FileQuestionComponent extends QuestionComponent<FileQuestion> implements ControlValueAccessor {
	private static readonly MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

	constructor(
		private apiService: ApiService,
		private notificationService: NotificationService,
		private translocoService: TranslocoService,
	) {
		super();
	}

	fileName: string | null = null;
	fileSizeBytes: number | null = null;
	uploadBusy = false;

	onRemoveFile = async (event: Event): Promise<void> => {
		event.stopPropagation();
		if (this.uploadBusy) {
			return;
		}
		await this.setAnswers();
		this.fileName = null;
		this.fileSizeBytes = null;
	};
	pickFile = async (event: Event): Promise<void> => {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		if (!file) {
			return;
		}

		if (file.size > FileQuestionComponent.MAX_FILE_SIZE_BYTES) {
			this.notificationService.showErrorToast(this.translocoService.translate("FILE_QUESTION__FILE_TOO_LARGE"));
			input.value = "";
			return;
		}

		this.uploadBusy = true;
		try {
			const buffer = await file.arrayBuffer();
			const uploadFile = new File([buffer], file.name, { type: file.type || "application/octet-stream" });
			const body = await firstValueFrom(
				this.apiService.uploadFiles$([uploadFile]).pipe(
					filter(
						(httpEvent: HttpEvent<FileResponse>): httpEvent is HttpResponse<FileResponse> =>
							httpEvent.type === HttpEventType.Response,
					),
					map((httpResponse) => httpResponse.body),
				),
			);
			const uploaded = body.data[0];
			let label = file.name;
			if (uploaded.currentFilename) {
				label = uploaded.currentFilename;
			} else if (uploaded.name && uploaded.extension) {
				label = `${uploaded.name}.${uploaded.extension}`;
			}
			this.fileName = label;
			this.fileSizeBytes = typeof file.size === "number" ? file.size : null;
			await this.setAnswers(uploaded.id, label);
		} catch {
			this.notificationService.showErrorToast(this.translocoService.translate("FILE_UPLOAD__ERROR_MESSAGE"));
		} finally {
			this.uploadBusy = false;
			input.value = "";
		}
	};
	get fileSizeLabel(): string | null {
		if (this.fileSizeBytes == null || this.fileSizeBytes < 0) {
			return null;
		}
		return this.formatByteSize(this.fileSizeBytes);
	}

	get hasFile(): boolean {
		return (this.answer?.answers?.length ?? 0) > 0;
	}

	onDropzoneActivate(fileInput: HTMLInputElement): void {
		this.activateFileInput(fileInput);
	}

	onSelectedActivate(fileInput: HTMLInputElement): void {
		this.activateFileInput(fileInput);
	}

	private activateFileInput(fileInput: HTMLInputElement): void {
		if (this.uploadBusy) {
			return;
		}
		fileInput.click();
	}

	get accept(): string {
		const allowedExtensions = this.question.allowedExtensions;
		return allowedExtensions?.length ? allowedExtensions.map((extension) => `.${extension}`).join(",") : "*/*";
	}

	override writeValue(answer: FormQuestion | undefined): void {
		super.writeValue(answer);
		const answers = answer?.answers;
		const fileId = answers?.[0];
		if (!fileId) {
			this.fileName = null;
			this.fileSizeBytes = null;
			return;
		}
		const displayFromSecond = answers[1]?.trim();
		if (displayFromSecond) {
			this.fileName = displayFromSecond;
			this.fileSizeBytes = null;
			return;
		}
		if (isLikelyFileDocumentId(fileId)) {
			this.fileName = null;
			this.fileSizeBytes = null;
			return;
		}
		this.fileName = fileId.split("/").pop() ?? null;
		this.fileSizeBytes = null;
	}

	private formatByteSize(bytes: number): string {
		if (bytes < 1024) {
			return `${bytes} B`;
		}
		const kilobytes = bytes / 1024;
		if (kilobytes < 1024) {
			const kilobyteDecimals = kilobytes >= 100 ? 0 : 1;
			return `${kilobytes.toFixed(kilobyteDecimals)} KB`;
		}
		const megabytes = kilobytes / 1024;
		const megabyteDecimals = megabytes >= 10 ? 1 : 2;
		return `${megabytes.toFixed(megabyteDecimals)} MB`;
	}
}
